import { uniqBy } from 'lodash';

import { EMBARGOED_COUNTRIES } from './constants';
import type { Country } from '../../repositories';
import type { SnakeToCamelCase } from '../../utils/types';

export class Countries {
  static provinceCountries = ['CN', 'CA', 'IN'];

  // for those countries we want to show tax input for single bill functionality
  static singleBillTaxCountries = ['AU', 'CA', 'IN'];

  private countriesExemptFromB2bNotification = ['US', 'IN'];

  private embargoedCountries = EMBARGOED_COUNTRIES;

  private countries: SnakeToCamelCase<Country>[];

  private readonly EU_TAX_TYPE = 'vat';

  constructor(countries: Country[]) {
    const normalizedCountries = uniqBy(
      countries
        .filter((countryItem) => !this.embargoedCountries.includes(countryItem.code))
        .map((countryItem) => ({
          code: countryItem.code,
          display: countryItem.display,
          showStateProvs: countryItem.show_state_provs,
          stateProvLabel: countryItem.state_prov_label,
          stateProvs: countryItem.state_provs,
          showTaxExemptionNumber: countryItem.show_tax_exemption_number,
          taxExemptionType: countryItem.tax_exemption_type,
          taxExemptionLabel: countryItem.tax_exemption_label,
          showTaxExemptionAgreement: countryItem.show_tax_exemption_agreement,
          taxExemptionDisclaimer: countryItem.tax_exemption_disclaimer,
        })),
      'code'
    );

    this.countries = normalizedCountries;
  }

  public getCountries() {
    return this.countries;
  }

  public getCountryLabel(countryCode: string) {
    return this.countries.find((c) => c.code === countryCode)?.display;
  }

  private getCountryByCode(countryCode: string) {
    return this.countries.find((country) => country.code === countryCode);
  }

  public getStates(countryCode: string) {
    const country = this.getCountryByCode(countryCode);

    if (!country) return [];

    return country.stateProvs;
  }

  /**
   * Should address inputs for this country display a "state/province" field?
   *
   * If you want to differentiate between countries that display "state" vs "province",
   * see `getCountryHasProvinces` / `getCountryHasStates`.
   */
  public isShowingStates(countryCode: string) {
    const country = this.getCountryByCode(countryCode);

    if (!country) return false;

    return country.showStateProvs;
  }

  public getStateLabel(countryCode: string) {
    const country = this.getCountryByCode(countryCode);

    if (!country?.stateProvLabel) return '';

    return country.stateProvLabel;
  }

  public isShowingTaxAgreement(countryCode: string) {
    const country = this.getCountryByCode(countryCode);

    if (!country) return false;

    return country.showTaxExemptionAgreement;
  }

  public isShowingTaxNumber(countryCode: string) {
    const country = this.getCountryByCode(countryCode);

    if (!country) return false;

    return country.showTaxExemptionNumber;
  }

  public isShowingSingleBillTaxNumber(countryCode: string) {
    const country = this.getCountryByCode(countryCode);

    if (!country) return false;

    return Countries.singleBillTaxCountries.includes(countryCode);
  }

  public getTaxDisclaimer(countryCode: string) {
    const country = this.getCountryByCode(countryCode);

    if (!country) return '';

    return country.taxExemptionDisclaimer ?? '';
  }

  public getTaxLabel(countryCode: string) {
    const country = this.getCountryByCode(countryCode);

    if (!country) return '';

    return country.taxExemptionLabel ?? '';
  }

  public getTaxType(countryCode: string) {
    const country = this.getCountryByCode(countryCode);

    if (!country) return '';

    return country.taxExemptionType ?? '';
  }

  public getIsVatEu(countryCode: string) {
    const country = this.getCountryByCode(countryCode);

    if (!country) return false;

    return this.getTaxType(countryCode) === this.EU_TAX_TYPE && this.isShowingTaxNumber(countryCode);
  }

  /**
   * Does this country has subdivisions that it calls "provinces"?
   */
  public getCountryHasProvinces(countryCode: string) {
    return this.isShowingStates(countryCode) && Countries.provinceCountries.includes(countryCode);
  }

  /**
   * Does this country has subdivisions that it calls "states"?
   */
  public getCountryHasStates(countryCode: string) {
    return this.isShowingStates(countryCode) && !Countries.provinceCountries.includes(countryCode);
  }

  /**
   * Does this country require a B2B notification?
   */
  public isB2bNotificationCountry(countryCode: string) {
    return !this.countriesExemptFromB2bNotification.includes(countryCode);
  }
}
